import { CategoryDetails } from '@/models/category-details/category-details';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AppService } from '@services/app.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil } from 'rxjs';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-add-category',
  templateUrl: './add-category.component.html',
  styleUrl: './add-category.component.scss'
})
export class AddCategoryComponent implements OnInit, OnDestroy {

  categoryForm!: FormGroup;
  categoryList: CategoryDetails[] = [];
  destroy$ = new Subject<boolean>();
  editId: number | null = null;
  searchText: any;
  states = ['Tamil Nadu', 'South India', 'North India'];
  rateDetails = ['FLAT_RATE', 'PER_PRODUCT'];
  isEditMode: boolean = false;


  constructor(
    private fb: FormBuilder,
    private appService: AppService,
    private spinner: NgxSpinnerService,
    private toster: ToastrService
  ) { }

  /* ---------------- INIT ---------------- */
  ngOnInit(): void {

    this.categoryForm = this.fb.group({
      categoryId: [0],
      categoryName: ['', [
        Validators.required,
        Validators.minLength(3),
        Validators.pattern(/^[A-Za-z ]+$/)
      ]],
      isBulkInsert: [false, Validators.required],
      isCodAvailable: [false, Validators.required],
      codDetailsList: this.fb.array([]),
      shippingList: this.fb.array([]),
      subCategories: this.fb.array([]),
      status: ['Active']
    });

    this.addCodRow();
    this.addShippingRow();
    this.addSubCategoryRow();
    this.getAll();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  /* ---------------- GETTERS ---------------- */

  get codDetailsList(): FormArray {
    return this.categoryForm.get('codDetailsList') as FormArray;
  }

  get shippingList(): FormArray {
    return this.categoryForm.get('shippingList') as FormArray;
  }

  get subCategories(): FormArray {
    return this.categoryForm.get('subCategories') as FormArray;
  }

  get subCategoryList(): FormArray {
    return this.categoryForm.get('subCategoryList') as FormArray;
  }

  createSubCategoryRow(data?: any): FormGroup {
    return this.fb.group({
      subCategoryId: [data?.subCategoryId || 0],

      // Control name must match HTML formControlName
      subCategoryName: [data?.subCategoryName || ''],
      status: ['Active']
    });
  }

  /* =========================================================
     COD SECTION
  ========================================================= */

  createCodRow(): FormGroup {
    return this.fb.group({
      codAmount: [0, [Validators.required, Validators.min(0)]],
      additionalAmount: [0, [Validators.required, Validators.min(0)]],
    }, { validators: this.rangeValidator });
  }

  addCodRow() {
    this.codDetailsList.push(this.createCodRow());
  }

  addSubCategoryRow(): void {
    this.subCategories.push(this.createSubCategoryRow());
  }


  removeCodRow(index: number) {
    const codGroup = this.codDetailsList.at(index) as FormGroup;
    const codId = codGroup.get('codDetailsId')?.value;

    // SERVER delete (already saved)
    if (codId && codId > 0) {
      this.spinner.show();

      this.appService.categoryCodDetailRemove(codId).subscribe({
        next: () => {
          this.codDetailsList.removeAt(index);
          this.spinner.hide();
          this.toster.success('COD details removed successfully');
        },
        error: () => {
          this.spinner.hide();
          this.toster.error('Failed to remove COD details');
        }
      });
    }
    // LOCAL delete (not saved yet)
    else {
      this.codDetailsList.removeAt(index);
    }
  }

  addSubCategory(name: string = '') {
    this.subCategories.push(this.createSubCategoryRow(name));
  }

  removeSubCategoryRow(index: number): void {

    const subCatGroup = this.subCategories.at(index) as FormGroup;
    const subCategoryId = subCatGroup.get('subCategoryId')?.value;

    if (subCategoryId && subCategoryId > 0) {
      this.spinner.show();

      this.appService.subCategoryRemove(subCategoryId).subscribe({
        next: () => {
          this.subCategories.removeAt(index);
          this.spinner.hide();
          this.toster.success('Sub Category removed successfully');
        },
        error: () => {
          this.spinner.hide();
          this.toster.error('Failed to remove Sub Category');
        }
      });
    } else {
      this.subCategories.removeAt(index);
    }
  }


  hasDuplicateSubCategory(): boolean {

    const names = this.subCategories.controls
      .map(c => c.get('subCategoryName')?.value?.trim().toLowerCase())
      .filter(v => v);

    return new Set(names).size !== names.length;
  }

  onSubCategoryInput(event: Event, index: number) {

    const input = event.target as HTMLInputElement;

    let value = input.value.replace(/[^a-zA-Z ]/g, '');
    value = value.replace(/\s+/g, ' ').trimStart();
    value = value.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());

    this.subCategories.at(index).get('subCategoryName')
      ?.setValue(value, { emitEvent: false });
  }

  hasDuplicateCodRange(): boolean {
    if (!this.categoryForm.get('isCodAvailable')?.value) {
      return false;
    }

    const ranges = this.codDetailsList.controls.map(
      c => `${c.value.productStartRange}-${c.value.productEndRange}`
    );

    return new Set(ranges).size !== ranges.length;
  }

  /* =========================================================
     SHIPPING SECTION
  ========================================================= */

  createShippingRow(): FormGroup {
    return this.fb.group({
      shippingPartner: ['', Validators.required],
      shippingType: ['FREE', Validators.required],
      state: ['Tamil Nadu', Validators.required],
      shippingAmount: [0, [Validators.required, Validators.min(0)]],
      productStartRange: [0, [Validators.required, Validators.min(0)]],
      productEndRange: [0, [Validators.required, Validators.min(0)]],
      isActive: [true],
      rateType: ['FLAT_RATE', Validators.required],
    }, { validators: this.rangeValidator }); // IMPORTANT FIX
  }

  addShippingRow() {
    this.shippingList.push(this.createShippingRow());
  }

  selectCodValue(event: any) {
    const isCodAvailable = event.target.value === 'true' || event.target.value === true;

    if (!isCodAvailable) {
      // COD = YES → add one row if empty
      if (this.codDetailsList.length === 0) {
        this.addCodRow();
      }

      // enable validators
      this.codDetailsList.controls.forEach(ctrl => {
        ctrl.enable();
        ctrl.updateValueAndValidity();
      });

    } else {
      // COD = NO → clear all COD rows
      this.codDetailsList.clear();
    }
  }

  removeShippingRow(index: number) {
    const shipGroup = this.shippingList.at(index) as FormGroup;
    const shippingId = shipGroup.get('shippingId')?.value;

    // SERVER delete
    if (shippingId && shippingId > 0) {
      this.spinner.show();

      this.appService.categoryShippingDetailsRemove(shippingId).subscribe({
        next: () => {
          this.shippingList.removeAt(index);
          this.spinner.hide();
          this.toster.success('Shipping details removed successfully');
        },
        error: () => {
          this.spinner.hide();
          this.toster.error('Failed to remove shipping details');
        }
      });
    }
    //  LOCAL delete
    else {
      this.shippingList.removeAt(index);
    }
  }


  hasDuplicateShippingRange(index: number): boolean {

    const list = this.shippingList.controls;
    const current = list[index];

    const partner = current.get('shippingPartner')?.value;
    const start = current.get('productStartRange')?.value;
    const end = current.get('productEndRange')?.value;

    if (!partner || start === null || end === null) {
      return false;
    }

    return list.some((ctrl, i) =>
      i !== index &&
      ctrl.get('shippingPartner')?.value === partner &&
      ctrl.get('productStartRange')?.value === start &&
      ctrl.get('productEndRange')?.value === end
    );
  }

  getDuplicateShippingPartner(index: number): string | null {

    const list = this.shippingList.controls;
    const current = list[index] as FormGroup;

    const partnerRaw = current.get('shippingPartner')?.value;
    const state = current.get('state')?.value;
    const rateType = current.get('rateType')?.value;
    const start = current.get('productStartRange')?.value;
    const end = current.get('productEndRange')?.value;

    // Don't validate if fields incomplete
    if (!partnerRaw || !state || !rateType || start === null || end === null) {
      return null;
    }

    const partner = partnerRaw.trim().toLowerCase();

    const duplicate = list.find((ctrl, i) => {

      if (i === index) return false;

      const ctrlPartnerRaw = ctrl.get('shippingPartner')?.value;
      const ctrlState = ctrl.get('state')?.value;
      const ctrlRateType = ctrl.get('rateType')?.value;
      const ctrlStart = ctrl.get('productStartRange')?.value;
      const ctrlEnd = ctrl.get('productEndRange')?.value;

      if (!ctrlPartnerRaw) return false;

      const ctrlPartner = ctrlPartnerRaw.trim().toLowerCase();

      return (
        ctrlPartner === partner &&
        ctrlState === state &&
        ctrlRateType === rateType &&
        Number(ctrlStart) === Number(start) &&
        Number(ctrlEnd) === Number(end)
      );
    });

    // Return readable name for UI message
    return duplicate ? partnerRaw : null;
  }



  hasAnyDuplicateShipping(): boolean {
    return this.shippingList.controls.some((_, i) =>
      this.getDuplicateShippingPartner(i)
    );
  }

  hasDuplicateShipping(): boolean {
    return this.shippingList.controls.some((_, i) => this.getDuplicateShippingPartner(i));
  }

  /* =========================================================
     COMMON RANGE VALIDATOR
  ========================================================= */

  rangeValidator(control: AbstractControl) {

    const start = control.get('productStartRange')?.value;
    const end = control.get('productEndRange')?.value;

    if (start !== null && end !== null && end <= start) {
      return { rangeInvalid: true };
    }

    return null;
  }

  /* =========================================================
     GET ALL
  ========================================================= */

  getAll() {

    this.spinner.show();

    this.appService.getAllCategoryDetails()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          this.categoryList = res.sort((a, b) => b.categoryId - a.categoryId);
          this.spinner.hide();
        },
        error: () => {
          this.spinner.hide();
          this.toster.error('Failed to load data');
        }
      });
  }

  /* =========================================================
     INPUT FORMAT
  ========================================================= */

  onCategoryInput(event: Event) {

    const input = event.target as HTMLInputElement;

    let value = input.value.replace(/[^a-zA-Z ]/g, '');
    value = value.replace(/\s+/g, ' ').trimStart();
    value = value.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());

    this.categoryForm.get('categoryName')?.setValue(value, { emitEvent: false });
  }

  /* =========================================================
     SAVE
  ========================================================= */

  save() {
    this.categoryForm.markAllAsTouched();
    const isCodAvailable = this.categoryForm.get('isCodAvailable')?.value;

    // BASIC FORM
    if (
      this.categoryForm.get('categoryName')?.invalid ||
      this.categoryForm.get('isBulkInsert')?.invalid ||
      this.categoryForm.get('isCodAvailable')?.invalid
    ) {
      return;
    }

    // COD VALIDATION ONLY WHEN YES
    if (isCodAvailable) {

      if (this.codDetailsList.length === 0) {
        this.toster.error('Please add COD details');
        return;
      }

      if (this.codDetailsList.invalid) {
        this.toster.error('Please fill valid COD details');
        return;
      }

      if (this.hasDuplicateCodRange()) {
        this.toster.error('Duplicate COD range not allowed');
        return;
      }
    }

    // SUB CATEGORY VALIDATION
    if (this.hasDuplicateSubCategory()) {
      this.toster.error('Duplicate Sub Category not allowed');
      return;
    }

    // SHIPPING
    if (this.shippingList.invalid || this.hasAnyDuplicateShipping()) {
      this.toster.error('Invalid shipping details');
      return;
    }

    const payload = {
      ...this.categoryForm.value,
      codAvailable: isCodAvailable,
      codDetailsList: isCodAvailable ? this.codDetailsList.value : [],
      subCategories: this.subCategories.value.map((s: any) => ({
        subCategoryId: s.subCategoryId || 0,
        categoryId: this.editId || 0,
        subCategoryName: s.subCategoryName,
        active: true,
        status: 'Active',
      }))
    };


    this.spinner.show();


    if (this.editId) {

      this.appService.updateCategoryDetails(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: res => {

            const index = this.categoryList.findIndex(x => x.categoryId === res.categoryId);
            if (index !== -1) this.categoryList[index] = res;

            this.afterSave('Updated successfully');
          },
          error: err => this.handleError(err)
        });

    } else {

      this.appService.addCategoryDetails(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: res => {
            this.categoryList.unshift(res);
            this.afterSave('Saved successfully');
          },
          error: err => this.handleError(err)
        });
    }
  }

  /* =========================================================
     EDIT
  ========================================================= */

  edit(row: CategoryDetails) {

    this.editId = row?.categoryId;
    this.isEditMode = true;

    this.codDetailsList.clear();
    this.shippingList.clear();
    this.subCategories.clear();

    row.codDetailsList?.forEach(c => {
      this.codDetailsList?.push(this.fb.group(c, { validators: this.rangeValidator }));
    });

    row.shippingList?.forEach(s => {
      this.shippingList?.push(this.fb.group(s, { validators: this.rangeValidator }));
    });

    row.subCategories?.forEach(sc => {
      this.subCategories.push(this.fb.group(sc));
    });

    this.categoryForm.patchValue(row);

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  /* =========================================================
     COMMON
  ========================================================= */

  afterSave(msg: string) {
    this.spinner.hide();
    this.isEditMode = false;
    this.toster.success(msg);
    this.resetForm();
    // window.location.reload();
  }

  handleError(err: any) {
    this.spinner.hide();
    this.toster.error(err?.error?.message || 'Something went wrong');
  }

  resetForm() {

    this.editId = null;

    this.categoryForm.reset({
      categoryId: 0,
      categoryName: '',
      isBulkInsert: false,
      isCodAvailable: true
    });

    this.codDetailsList.clear();
    this.shippingList.clear();
    this.subCategories.clear();

    this.addCodRow();
    this.addShippingRow();
    this.addSubCategory();
  }

  onStatusChange(category: any, newStatus: string): void {
    const oldStatus = category.status;
    if (oldStatus === newStatus) {
      return;
    }
    Swal.fire({
      title: 'Are you sure?',
      text: `Change status to "${newStatus}"?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      allowOutsideClick: false,
      allowEscapeKey: false,
      backdrop: true,

      confirmButtonColor: '#28a745',
      cancelButtonColor: '#d33'
    }).then((result) => {
      if (result.isConfirmed) {
        const payload = {
          categoryId: category.categoryId,
          status: newStatus
        };
        this.appService.updateCategoryStatus(payload).subscribe({
          next: (res: any) => {
            category.status = newStatus;
            Swal.fire({
              icon: 'success',
              title: 'Updated',
              text: 'Category status updated successfully.',
              timer: 1500,
              showConfirmButton: false
            });
            this.getAll();
          },
          error: () => {
            category.status = oldStatus;
            Swal.fire(
              'Error',
              'Unable to update status.',
              'error'
            );
          }
        });
      } else {
        category.status = oldStatus;
      }
    });
  }
}


